import type { AvatarResponse } from '@innovationdepartment/proxima-sdk-axios';
import { useCallback, useMemo, useState } from 'react';

export type AgeRange = [number, number];

export type AvatarSortKey = 'age_asc' | 'age_desc';

export const AVATARS_PAGE_SIZE = 20;

const DEFAULT_BOUNDS: AgeRange = [0, 100];

const byName = (a: AvatarResponse, b: AvatarResponse) => a.displayName.localeCompare(b.displayName);
const byAge = (a: AvatarResponse, b: AvatarResponse) => a.age - b.age;

/** Orders by age in the key's direction, with display name ascending as the tie-breaker. */
const compareBySortKey =
  (sortKey: AvatarSortKey) =>
  (a: AvatarResponse, b: AvatarResponse): number =>
    sortKey === 'age_asc' ? byAge(a, b) || byName(a, b) : byAge(b, a) || byName(a, b);

/**
 * Client-side filtering, sorting, and pagination of the avatar list: an age
 * range (bounds derived from the data), a gender selection (empty = all
 * genders), and a sort key (oldest first by default). Changing a filter or
 * the sort resets to the first page.
 */
const useAvatarFilters = (avatars: AvatarResponse[]) => {
  const ageBounds = useMemo<AgeRange>(() => {
    if (!avatars.length) return DEFAULT_BOUNDS;
    const ages = avatars.map(avatar => avatar.age);
    return [Math.min(...ages), Math.max(...ages)];
  }, [avatars]);

  // null = no age filter applied (full bounds)
  const [selectedAgeRange, setSelectedAgeRange] = useState<AgeRange | null>(null);
  const [selectedGenders, setSelectedGenders] = useState<string[]>([]);
  const [selectedSortKey, setSelectedSortKey] = useState<AvatarSortKey>('age_desc');
  const [pageNumber, setPageNumber] = useState(1);

  // Clamp a saved selection into the current bounds — the library can reload
  // with a narrower age spread than when the user set the filter.
  const ageRange = useMemo<AgeRange>(() => {
    if (!selectedAgeRange) return ageBounds;
    const low = Math.min(Math.max(selectedAgeRange[0], ageBounds[0]), ageBounds[1]);
    const high = Math.max(Math.min(selectedAgeRange[1], ageBounds[1]), low);
    return [low, high];
  }, [selectedAgeRange, ageBounds]);

  const setAgeRange = useCallback((range: AgeRange) => {
    setSelectedAgeRange(range);
    setPageNumber(1);
  }, []);

  const toggleGender = useCallback((gender: string) => {
    setSelectedGenders(prev =>
      prev.includes(gender) ? prev.filter(item => item !== gender) : [...prev, gender]
    );
    setPageNumber(1);
  }, []);

  const setSortKey = useCallback((key: AvatarSortKey) => {
    setSelectedSortKey(key);
    setPageNumber(1);
  }, []);

  const setPage = useCallback((page: number) => setPageNumber(page), []);

  const filteredAvatars = useMemo(
    () =>
      avatars
        .filter(
          avatar =>
            avatar.age >= ageRange[0] &&
            avatar.age <= ageRange[1] &&
            (selectedGenders.length === 0 || selectedGenders.includes(avatar.gender))
        )
        .sort(compareBySortKey(selectedSortKey)),
    [avatars, ageRange, selectedGenders, selectedSortKey]
  );

  // Clamp so shrinking results (or a shrinking library) never strand the
  // user on a page past the end.
  const totalPages = Math.max(1, Math.ceil(filteredAvatars.length / AVATARS_PAGE_SIZE));
  const currentPage = Math.min(pageNumber, totalPages);

  const pageAvatars = useMemo(
    () =>
      filteredAvatars.slice((currentPage - 1) * AVATARS_PAGE_SIZE, currentPage * AVATARS_PAGE_SIZE),
    [filteredAvatars, currentPage]
  );

  return {
    ageBounds,
    ageRange,
    setAgeRange,
    selectedGenders,
    toggleGender,
    sortKey: selectedSortKey,
    setSortKey,
    filteredAvatars,
    pageAvatars,
    totalPages,
    currentPage,
    setPage
  };
};

export default useAvatarFilters;
